import i18next from "i18next";
import axios from "axios";
import { refreshEverything } from "@/lib/queryClient";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE, withCredentials: true });
axios.defaults.withCredentials = true;

api.interceptors.response.use(
  (r) => {
    window.dispatchEvent(new Event("gf:api-up"));
    if (!["get", "head", "options"].includes((r.config?.method || "get").toLowerCase()) && !(r.config?.url || "").includes("/ai/")) refreshEverything();
    return r;
  },
  async (err) => {
    // Network hiccup (workshop wifi): retry idempotent calls once after 1.5s, and show the offline banner.
    if (!err.response && err.config && !err.config.__retried && ["get", "put", "delete"].includes((err.config.method || "").toLowerCase())) {
      window.dispatchEvent(new Event("gf:api-down"));
      err.config.__retried = true;
      await new Promise((res) => setTimeout(res, 1500));
      return api.request(err.config);
    }
    if (!err.response) window.dispatchEvent(new Event("gf:api-down"));
    if (err?.response?.status === 401 && window.location.pathname !== "/login") {
      localStorage.removeItem("garage_session");
      localStorage.removeItem("garage_user");
      window.location.href = "/login";
    }
    if (err?.response?.status >= 500 && !err?.response?.data?.detail) {
      const path = (err.config?.url || "").replace(/^.*\/api/, "/api").split("?")[0];
      err.message = `${path} — serverfout (${err.response.status})`;
    }
    return Promise.reject(err);
  }
);

export function formatEUR(v, locale = "de-DE") {
  const n = Number(v || 0);
  return new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(n);
}

/** Masked price placeholder shown to staff who lack the "prices.*" scope for
 *  a given section.  Owner has full access via role bypass — this only kicks
 *  in for staff members whose permission the owner has withheld. */
export const HIDDEN_PRICE = "€ ••••";

/** One-liner used all over the app: `€ 45,00` for the eyes that may see it,
 *  `€ ••••` for the eyes the owner wants to keep out of the money. */
export function money(value, canSee, locale = "de-DE") {
  return canSee ? formatEUR(value, locale) : HIDDEN_PRICE;
}

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") {
    const codes = { card_locked: "errCardLocked", invoice_paid_locked: "errInvoicePaidLocked" };
    if (codes[d]) return i18next.t(codes[d]);
    if (d === "response_validation_error") {
      const e = err.response.data.errors?.[0];
      return `${err.response.data.path}: ${e ? `${e.loc?.slice(1).join(".")} — ${e.msg}` : "invalid data"}`;
    }
    return d;
  }
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}
